
import { useCallback, useEffect, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError, UploadProgress } from '../api/client';
import { cutApi } from '../api/endpoints';
import type {
  CutCandidate,
  CutExtractRequest,
  CutRun,
  CutRunRequest,
  CutSource,
  Id,
  ToolTask,
} from '../types/api';
import { MAX_CUT_SOURCE_BYTES, isTaskFinished } from '../types/api';
import { pollIntervalFrom, queryKeys } from './keys';
import { useTaskTracker, type TaskTracker } from './useTasks';

export interface CutUpload {
  upload: UseMutationResult<CutSource, ApiError, File>;
  progress: number | null;
  reset: () => void;
}

export function useUploadCutSource(): CutUpload {
  const [progress, setProgress] = useState<number | null>(null);
  const upload = useMutation<CutSource, ApiError, File>({
    mutationFn: (file) =>
      cutApi.uploadSource(file, (event: UploadProgress) => setProgress(event.fraction)),
    retry: false,
    onMutate: () => setProgress(0),
    onSettled: () => setProgress(null),
  });
  const reset = useCallback(() => {
    upload.reset();
    setProgress(null);
  }, [upload]);
  return { upload, progress, reset };
}

export function validateCutSource(file: File | null): string[] {
  if (!file) return ['Файл не выбран'];
  const problems: string[] = [];
  if (file.size === 0) problems.push('Файл пустой');
  if (file.size > MAX_CUT_SOURCE_BYTES) {
    problems.push(`Файл больше ${Math.round(MAX_CUT_SOURCE_BYTES / 1024 ** 3)} ГБ`);
  }
  const looksLikeVideo =
    file.type.startsWith('video/') || /\.(mp4|mov|m4v|mkv|webm|avi|mpe?g)$/i.test(file.name);
  if (!looksLikeVideo) problems.push('Нужен видеофайл: mp4, mov, mkv или webm');
  return problems;
}

export function useAnalyzeCut(): UseMutationResult<CutRun, ApiError, CutRunRequest> {
  const qc = useQueryClient();
  return useMutation<CutRun, ApiError, CutRunRequest>({
    mutationFn: (payload) => cutApi.analyze(payload),
    retry: false,
    onSuccess: (run) => {
      qc.setQueryData(queryKeys.cutRun(run.run_id), run);
      void qc.invalidateQueries({ queryKey: queryKeys.tasksRoot });
    },
  });
}

export function isCutRunActive(run: CutRun | undefined): boolean {
  return Boolean(run) && !isTaskFinished(run?.status);
}

export function useCutRun(
  runId: Id | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<CutRun, ApiError> {
  return useQuery<CutRun, ApiError>({
    queryKey: queryKeys.cutRun(runId ?? ''),
    queryFn: () => cutApi.run(runId as Id),
    enabled: Boolean(runId) && (options.enabled ?? true),
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return isCutRunActive(query.state.data) ? pollIntervalFrom(undefined) : false;
    },
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export interface CutExtraction {
  extract: UseMutationResult<ToolTask, ApiError, CutExtractRequest>;
  task: TaskTracker;
  pendingIndex: number | null;
}

export function useExtractCandidate(runId: Id | undefined): CutExtraction {
  const qc = useQueryClient();
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const task = useTaskTracker(runId ? [queryKeys.cutRun(runId)] : []);
  const extract = useMutation<ToolTask, ApiError, CutExtractRequest>({
    mutationFn: (payload) => cutApi.extract(runId as Id, payload),
    retry: false,
    onMutate: (payload) => setPendingIndex(payload.index),
    onSuccess: (accepted) => {
      task.track(accepted.id);
      void qc.invalidateQueries({ queryKey: queryKeys.tasksRoot });
    },
    onError: () => setPendingIndex(null),
  });

  const finished = isTaskFinished(task.task.data?.status);
  useEffect(() => {
    if (finished) setPendingIndex(null);
  }, [finished]);

  return { extract, task, pendingIndex };
}

export function totalUsefulSeconds(candidates: readonly CutCandidate[]): number {
  return candidates.reduce((sum, item) => sum + item.duration_s, 0);
}
